import { motion } from "framer-motion";
import { Award, ShieldCheck, Waves, Users, Sparkles, Heart, Compass, PlayCircle, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/site/Navbar";
import { Seo } from "@/components/site/Seo";
import Footer from "@/components/site/Footer";
import Pillars from "@/components/site/Pillars";
import Partners from "@/components/site/Partners";
import About from "@/components/site/About";
import FinalCTA from "@/components/site/FinalCTA";
import WhereNext from "@/components/site/WhereNext";

const nextItems = [
  { sub: "See the full lesson pathway", label: "Our Swimming Lessons", to: "/classes" },
  { sub: "How lessons are structured", label: "What to Expect", to: "/how-it-works" },
  { sub: "The faces behind every splash", label: "Meet the Team", to: "/team" },
  { sub: "View prices & packages", label: "Pricing", to: "/pricing" },
  { sub: "Speak to our team", label: "Contact Us", to: "/contact" },
];

const stats = [
  { icon: <Award size={20} />, value: "Since 2014", label: "Trusted by local families" },
  { icon: <ShieldCheck size={20} />, value: "STA Swim Star", label: "Swim Teachers Association" },
  { icon: <Waves size={20} />, value: "Purpose-built", label: "Our own centre since 2018" },
  { icon: <Users size={20} />, value: "Small classes", label: "Personalised support" },
];

const story = [
  {
    icon: <Sparkles size={20} />,
    accent: "#FF7A1A",
    overline: "Our ethos",
    title: "Where confidence grows — one splash at a time",
    body: "We believe swimming is far more than a skill. It's a foundation for safety, confidence and joy. Our approach is baby- and child-led, nurturing each swimmer at their own pace with gentle guidance, encouragement and play. Every lesson is designed to help children feel secure, capable and proud of themselves — whether they're dipping their toes for the first time or mastering new techniques.",
  },
  {
    icon: <Heart size={20} />,
    accent: "#0EA5E9",
    overline: "Family first",
    title: "A family-first, community-rooted swim school",
    body: "Happy Tiddlers was built for families. Our warm, welcoming pool is a place where parents exhale, children thrive and everyone feels part of something special. Over the years, we've grown into a supportive community where friendships form, milestones are celebrated and confidence blossoms both in and out of the water.",
  },
  {
    icon: <Compass size={20} />,
    accent: "#14B8A6",
    overline: "Skills for life",
    title: "Teaching life lessons that last",
    body: "From water safety and resilience to independence and trust, our lessons go far beyond swimming. We're passionate about helping children build the courage to try, the confidence to grow and the joy to keep coming back — all in an environment where they feel loved, seen and supported.",
  },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#334155] font-body" data-testid="about-page">
      <Seo
        title="About Us — Family-Run Swim School Since 2014 | Happy Tiddlers"
        description="Meet Happy Tiddlers — a family-run, community-focused STA 'Swim Star' swim school in Barton-le-Clay, Bedfordshire, building happy swimmers since 2014."
        path="/about"
      />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden pt-16 pb-20 md:pt-24 md:pb-24" data-testid="about-hero">
        <div className="blob absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-[#FF7A1A]/10" />
        <div className="container mx-auto px-6 md:px-12 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <span className="text-sm text-[#0EA5E9] tracking-[0.2em] uppercase font-bold">About Happy Tiddlers</span>
              <h1 className="mt-4 font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-[#0F172A] leading-[1.05]">
                A swim school built on trust — since 2014
              </h1>
              <p className="mt-6 text-lg text-[#334155] leading-relaxed">
                For over a decade, Happy Tiddlers has been Barton-le-Clay's private, exclusive
                STA 'Swim Star' school — a warm, purpose-built home where thousands of babies,
                children and adults have fallen in love with the water. We're family-run and
                community-rooted, here for every family from the very first nervous splash to
                that proud, capable first swim.
              </p>
              <p className="mt-4 text-lg text-[#334155] leading-relaxed">
                Our approach is gently <span className="font-semibold text-[#0A2540]">child-led</span> — we move at your little
                one's pace, celebrating every small win and never forcing submersions. It's an approach
                that's transformative for <span className="font-semibold text-[#0A2540]">nervous swimmers</span>, replacing
                anxiety with genuine confidence and a real love of the water. Because our vision is simple:
                to help as many people as possible fall in love with the water and gain skills that could
                one day save their life.
              </p>
              <p className="mt-4 text-lg text-[#334155] leading-relaxed">
                Everything we do rests on four simple promises: <span className="font-semibold text-[#0A2540]">confidence before technique</span>,
                <span className="font-semibold text-[#0A2540]"> safety you can trust completely</span>,
                <span className="font-semibold text-[#0A2540]"> joyful lessons that make learning stick</span>, and
                <span className="font-semibold text-[#0A2540]"> skills that last a lifetime</span>. It's more than
                swimming — it's where confidence begins, friendships grow and families become part
                of something special.
              </p>
              <Link
                to="/team"
                className="group mt-7 inline-flex items-center gap-2 font-bold text-[#0EA5E9] hover:gap-3 transition-all"
                data-testid="about-meet-team-link"
              >
                <Users size={18} /> Meet the team behind it all
                <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="rounded-[2.5rem] overflow-hidden shadow-2xl shadow-[#0A2540]/20 rotate-2 hover:rotate-0 transition-transform duration-500"
            >
              <img
                src={`${process.env.PUBLIC_URL}/about-hero.jpg`}
                alt="The Happy Tiddlers family enjoying a day at the beach"
                className="w-full h-[380px] md:h-[520px] object-cover object-top"
              />
            </motion.div>
          </div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {stats.map((s, i) => (
              <motion.div
                key={s.value}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm"
                data-testid={`about-stat-${i}`}
              >
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#0EA5E9]/10 text-[#0EA5E9] mb-4">{s.icon}</span>
                <p className="font-heading font-extrabold text-xl text-[#0F172A] leading-none">{s.value}</p>
                <p className="mt-1.5 text-sm text-[#64748B]">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our story / ethos */}
      <section className="pb-8 md:pb-12" data-testid="about-story">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {story.map((s, i) => (
              <motion.div
                key={s.overline}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="bg-white rounded-[2rem] p-8 md:p-9 border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500 flex flex-col"
                data-testid={`about-story-${i}`}
              >
                <span
                  className="grid place-items-center w-12 h-12 rounded-2xl text-white mb-5"
                  style={{ backgroundColor: s.accent }}
                >
                  {s.icon}
                </span>
                <span className="text-xs tracking-[0.2em] uppercase font-bold" style={{ color: s.accent }}>
                  {s.overline}
                </span>
                <h2 className="mt-2 font-heading text-xl md:text-2xl font-bold tracking-tight text-[#0F172A] leading-snug">
                  {s.title}
                </h2>
                <p className="mt-4 text-[#334155] leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* See us in action */}
      <section className="pb-8 md:pb-12 pt-4" data-testid="about-video">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-8">
            <span className="inline-flex items-center gap-2 text-sm text-[#FF7A1A] tracking-[0.2em] uppercase font-bold">
              <PlayCircle size={16} /> See us in action
            </span>
            <h2 className="mt-4 font-heading text-3xl md:text-4xl font-bold tracking-tight text-[#0F172A] leading-tight">
              A little splash of Happy Tiddlers
            </h2>
          </div>
          <div className="relative w-full overflow-hidden rounded-[2rem] shadow-2xl shadow-[#0A2540]/20 border border-slate-100" style={{ aspectRatio: "16 / 9" }}>
            <iframe
              className="absolute inset-0 w-full h-full"
              src="https://www.youtube-nocookie.com/embed/JOn0BNr_Bos?rel=0"
              title="Happy Tiddlers Swim School"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              data-testid="about-video-embed"
            />
          </div>
        </div>
      </section>

      <Pillars />
      <Partners />
      <About />
      <FinalCTA />
      <WhereNext items={nextItems} />
      <Footer />
    </div>
  );
}
